import { useState, useMemo } from "react";
import { Grid3x3, Trash2, Download } from "lucide-react";
import { toast } from "react-toastify";
import { useDeleteChipPos } from "@/hooks/useChipPos";
import { SWRApiError } from "@/hooks/swr";
import { ChipPos } from "@/types/chipPos";
import { Mask } from "@/types/mask";
import { Button } from "@/components/ui/button";
import { InfoAlert } from "../../shared/Alerts";
import DeleteActionDialog from "../../shared/DeleteActionDialog";
import { ChipPosEditorDialog } from "../../chipPos/ChipPosEditorDialog";
import { parseChipPosFile, ParsedChipPos } from "../../chipPos/chipPosConverter";
import { StepCardWrapper } from "./StepCardWrapper";
import { downloadFileByUrl } from "@/lib/utils";

type ChipPosUploadStepProps = {
  chipPosError?: SWRApiError;
  chipPos: ChipPos[];
  isChipPosLoading: boolean;
  isLocked: boolean;
  refreshData: () => void;
  selectedMask?: Mask;
};
const ChipPosUploadStep = ({
  chipPosError,
  chipPos,
  isChipPosLoading,
  isLocked,
  refreshData,
  selectedMask,
}: ChipPosUploadStepProps) => {
  const { trigger: deleteChipPos, isMutating: isDeleting } = useDeleteChipPos();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingChipPos, setEditingChipPos] = useState<{
    data: ParsedChipPos;
    name: string;
  } | null>(null);
  const [chipPosToDelete, setChipPosToDelete] = useState<ChipPos | null>(null);

  const handleCreateChipPos = () => {
    setEditingChipPos(null);
    setIsEditorOpen(true);
  };

  const handleEditChipPos = (chipPosFile: ChipPos) => {
    if (!chipPosFile.data) {
      toast.error("No data available for this chip_pos file");
      return;
    }

    try {
      const parsed = parseChipPosFile(chipPosFile.data);
      setEditingChipPos({
        data: parsed,
        name: chipPosFile.name,
      });
      setIsEditorOpen(true);
    } catch (error) {
      console.error("Failed to parse chip_pos file:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to parse chip_pos file";
      toast.error(errorMessage);
    }
  };

  const handleDownloadChipPos = (chipPosFile: ChipPos) => {
    if (!chipPosFile.data) {
      toast.error("No data available to download");
      return;
    }

    try {
      // Create a blob from the chip_pos data
      const blob = new Blob([chipPosFile.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      downloadFileByUrl(url, chipPosFile.name);
    } catch (error) {
      console.error("Failed to download chip_pos file:", error);
      toast.error("Failed to download chip_pos file");
    }
  };

  const handleEditorConfirm = () => {
    toast.success("Chip pos file saved successfully!");
    refreshData();
  };

  const handleDeleteChipPos = async () => {
    if (!chipPosToDelete) return;

    try {
      await deleteChipPos({ id: chipPosToDelete.id });
      toast.success("Chip pos file deleted successfully!");
      refreshData();
    } catch (error) {
      console.error("Failed to delete chip_pos file:", error);
      toast.error(error instanceof SWRApiError ? error.message : "Failed to delete chip_pos file");
    } finally {
      setChipPosToDelete(null);
    }
  };

  // Build mask data for the editor
  const editorMaskData = useMemo(() => {
    if (!selectedMask) return null;
    return {
      id: selectedMask.id,
      name: selectedMask.name,
      waferDiameterInMm: parseFloat(selectedMask.waferDiameter),
      dieWidth: selectedMask.dieWidth,
      dieHeight: selectedMask.dieHeight,
      dieOffsetX: selectedMask.dieOffsetX,
      dieOffsetY: selectedMask.dieOffsetY,
      referenceDieX: selectedMask.referenceDieX,
      referenceDieY: selectedMask.referenceDieY,
    };
  }, [selectedMask]);

  if (isChipPosLoading) {
    return (
      <StepCardWrapper title="Chip Position Files">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          Loading chip position files...
        </div>
      </StepCardWrapper>
    );
  }

  if (chipPosError) {
    return (
      <StepCardWrapper title="Chip Position Files">
        <div className="text-sm text-red-600">Error loading chip position files</div>
      </StepCardWrapper>
    );
  }

  return (
    <StepCardWrapper title="Chip Position Files">
      <div className="grid gap-4">
        {chipPos.length > 0 ? (
          <div className="grid gap-2">
            {chipPos.map((chipPosFile) => (
              <div
                key={chipPosFile.id}
                className="group flex items-center justify-between p-2 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={() => handleEditChipPos(chipPosFile)}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{chipPosFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {chipPosFile.data &&
                      (() => {
                        try {
                          return `${parseChipPosFile(chipPosFile.data).selectedDieIds.length} Dies`;
                        } catch (error) {
                          console.error("Failed to parse chip_pos file:", error);
                          return "Invalid - Verify supported format in docs";
                        }
                      })()}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 hover:bg-gray-300 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadChipPos(chipPosFile);
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-100 transition-opacity hover:text-red-700 hover:bg-red-50"
                    disabled={isLocked || isDeleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChipPosToDelete(chipPosFile);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <InfoAlert>No Chip Position Files linked. Create one using the button below.</InfoAlert>
        )}

        <Button
          onClick={handleCreateChipPos}
          disabled={isLocked || !selectedMask}
          className="w-full"
        >
          <Grid3x3 className="w-4 h-4 mr-2" />
          Create Chip Pos
        </Button>

        {editorMaskData && (
          <ChipPosEditorDialog
            open={isEditorOpen}
            onOpenChange={setIsEditorOpen}
            maskData={editorMaskData}
            initialData={editingChipPos?.data}
            initialFileName={editingChipPos?.name}
            onConfirm={handleEditorConfirm}
          />
        )}

        <DeleteActionDialog
          open={!!chipPosToDelete}
          onOpenChange={(open) => !open && setChipPosToDelete(null)}
          title="Delete Chip Position File"
          description={`Are you sure you want to delete "${chipPosToDelete?.name}"? This action cannot be undone.`}
          action={handleDeleteChipPos}
          isExecutingAction={isDeleting}
        />
      </div>
    </StepCardWrapper>
  );
};

export default ChipPosUploadStep;
